import { html, LitElement } from 'lit';
import { customElement } from 'lit/decorators';

import '@carbon/web-components/es/components/tile/tile.js';
import '@carbon/web-components/es/components/progress-indicator';
import '@carbon/web-components/es/components/code-snippet/code-snippet.js';

import { unsafeHTML } from 'lit-html/directives/unsafe-html';
import '../text-editor/pdq-text-editor';
import './PDQCodeHtml';
import CodeIcon from '@carbon/web-components/es/icons/code/16';


@customElement('pdq-tutorial-text-editor')
export class PDQTutorialTextEditor extends LitElement {
  render() {
    const codeStatements = `
<pdq-text-editor language="sql" height="250px" >
<script type="application/sql" slot="text">
CREATE TABLE Orders (
    order_id INTEGER PRIMARY KEY,
    user_id INTEGER,
    order_date TEXT
);
</script>
</pdq-text-editor>
`

    //return html`${unsafeHTML(codeStatements)}`

    return html`
      <cds-tile>
        <h3>PDQ Text Editor</h3>

        The <cds-tag type="green">${CodeIcon({ slot: 'icon' })}pdq-text-editor</cds-tag>
        can be used to embed the PDQ Text Editor in your application.
        
        The Monaco Editor is a browser-based code editor, foundational to 
        Microsoft’s VS Code. Monaco is optimized for performance and offers an 
        embeddable experience in web applications.
        
        
        
        <pdq-code-html code="${codeStatements}"></pdq-code-html>${unsafeHTML(codeStatements)}
        
        
        <h3><cds-tag type="green">${CodeIcon({ slot: 'icon' })}pdq-text-editor</cds-tag> Options</h3>
        <p>
          This component wraps the Monaco Editor, allowing for extensive customization.
          Below are the available properties and methods for configuring the component:
        </p>

        <h2>Properties</h2>
        <cds-table>
          
          <cds-table-head>
            <cds-table-header-row>
              <cds-table-header-cell>Property</cds-table-header-cell>
              <cds-table-header-cell>Type</cds-table-header-cell>
              <cds-table-header-cell>Default</cds-table-header-cell>
              <cds-table-header-cell>Description</cds-table-header-cell>
            </cds-table-header-row>
          </cds-table-head>
          <cds-table-body>
            <cds-table-row>
              <cds-table-cell><code>value</code></cds-table-cell>
              <cds-table-cell><code>string</code></cds-table-cell>
              <cds-table-cell><code>''</code></cds-table-cell>
              <cds-table-cell>Initial editor content.</cds-table-cell>
            </cds-table-row>
            <cds-table-row>
              <cds-table-cell><code>theme</code></cds-table-cell>
              <cds-table-cell><code>string</code></cds-table-cell>
              <cds-table-cell><code>'vs'</code></cds-table-cell>
              <cds-table-cell>Editor theme (<code>'vs'</code>, <code>'vs-dark'</code>, etc.).</cds-table-cell>
            </cds-table-row>
            <cds-table-row>
              <cds-table-cell><code>language</code></cds-table-cell>
              <cds-table-cell><code>string</code></cds-table-cell>
              <cds-table-cell><code>'json'</code></cds-table-cell>
              <cds-table-cell>Programming language for syntax highlighting.</cds-table-cell>
            </cds-table-row>
            <cds-table-row>
              <cds-table-cell><code>height</code></cds-table-cell>
              <cds-table-cell><code>string</code></cds-table-cell>
              <cds-table-cell><code>'100%'</code></cds-table-cell>
              <cds-table-cell>Height of the editor.</cds-table-cell>
            </cds-table-row>
            <cds-table-row>
              <cds-table-cell><code>modelUri</code></cds-table-cell>
              <cds-table-cell><code>string</code></cds-table-cell>
              <cds-table-cell><code>inmemory://monaco-<em>id</em></code></cds-table-cell>
              <cds-table-cell>URI for the editor's model.</cds-table-cell>
            </cds-table-row>
          </cds-table-body>
        </cds-table>

        <h2>Methods</h2>
        <h3><code>addJsonSchema(uri: string, schema: object, validate: boolean = true, fileMatch?: string)</code></h3>
        <p>Adds a JSON schema for validation.</p>
        <ul>
          <li><strong>uri</strong>: Schema identifier.</li>
          <li><strong>schema</strong>: JSON schema object.</li>
          <li><strong>validate</strong>: Enable validation (default: <code>true</code>).</li>
          <li><strong>fileMatch</strong>: Glob pattern to match files.</li>
        </ul>

        <h3><code>setModel(value: string, language: string, uri: string | Uri)</code></h3>
        <p>Updates the editor's model.</p>
        <ul>
          <li><strong>value</strong>: New content.</li>
          <li><strong>language</strong>: Language for syntax highlighting.</li>
          <li><strong>uri</strong>: Model URI.</li>
        </ul>

        <h3><code>setOptions(options: editor.IEditorOptions)</code></h3>
        <p>Updates editor options dynamically.</p>
        <ul>
          <li><strong>options</strong>: Monaco Editor configuration options.</li>
        </ul>

        <h3>Accessors</h3>
        <ul>
          <li><code>editor</code>: Returns the Monaco editor instance.</li>
          <li><code>model</code>: Returns the current editor model.</li>
          <li><code>selectedText</code>: Returns the currently selected text.</li>
        </ul>

        <h2>Attributes for Configuration</h2>
        <p>Set editor options via attributes. For example:</p>
        <pre><code>&lt;pdq-monaco line-numbers="on" word-wrap="on"&gt;&lt;/pdq-monaco&gt;</code></pre>
        <cds-table>
          <cds-table-head>
            <cds-table-header-row>
              <cds-table-header-cell>Attribute</cds-table-header-cell>
              <cds-table-header-cell>Maps to</cds-table-header-cell>
              <cds-table-header-cell>Description</cds-table-header-cell>
            </cds-table-header-row>
          </cds-table-head>
          <cds-table-body>
            <cds-table-row>
              <cds-table-cell><code>automatic-layout</code></cds-table-cell>
              <cds-table-cell><code>automaticLayout</code></cds-table-cell>
              <cds-table-cell>Adjust layout automatically.</cds-table-cell>
            </cds-table-row>
            <cds-table-row>
              <cds-table-cell><code>line-numbers</code></cds-table-cell>
              <cds-table-cell><code>lineNumbers</code></cds-table-cell>
              <cds-table-cell>Control line numbers (<code>'on'</code>, <code>'off'</code>, etc.).</cds-table-cell>
            </cds-table-row>
            <cds-table-row>
              <cds-table-cell><code>rounded-selection</code></cds-table-cell>
              <cds-table-cell><code>roundedSelection</code></cds-table-cell>
              <cds-table-cell>Enable rounded selection.</cds-table-cell>
            </cds-table-row>
            <cds-table-row>
              <cds-table-cell><code>scroll-beyond-last-line</code></cds-table-cell>
              <cds-table-cell><code>scrollBeyondLastLine</code></cds-table-cell>
              <cds-table-cell>Allow scrolling past the last line.</cds-table-cell>
            </cds-table-row>
            <cds-table-row>
              <cds-table-cell><code>read-only</code></cds-table-cell>
              <cds-table-cell><code>readOnly</code></cds-table-cell>
              <cds-table-cell>Set editor to read-only mode.</cds-table-cell>
            </cds-table-row>
            <cds-table-row>
              <cds-table-cell><code>word-wrap</code></cds-table-cell>
              <cds-table-cell><code>wordWrap</code></cds-table-cell>
              <cds-table-cell>Enable word wrapping (<code>'on'</code>, <code>'off'</code>).</cds-table-cell>
            </cds-table-row>
            <cds-table-row>
              <cds-table-cell><code>word-wrap-column</code></cds-table-cell>
              <cds-table-cell><code>wordWrapColumn</code></cds-table-cell>
              <cds-table-cell>Column for word wrap (integer).</cds-table-cell>
            </cds-table-row>
            <cds-table-row>
              <cds-table-cell><code>wrapping-indent</code></cds-table-cell>
              <cds-table-cell><code>wrappingIndent</code></cds-table-cell>
              <cds-table-cell>Wrapping indentation style (<code>'none'</code>, <code>'same'</code>).</cds-table-cell>
            </cds-table-row>
          </cds-table-body>
        </cds-table>

      </cds-tile>
    `;
  }
}

